/**
 * Pre-WPD crop review panel.
 *
 * Shows the original PDF page (with the crop bbox highlighted) alongside the
 * cropped panel.  The operator can drag a new rectangle on the source page to
 * correct a wrong crop; that correction is saved to S3 and logged as training
 * data.  Trace calibration data (axes + curve count) is shown when available.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import type { BatchOcr, BatchPanel, CropTrace } from "../../api/client";
import { AllTracersModal } from "./AllTracersModal";

interface Props {
  path: string;
  jobId: string;
  panel: BatchPanel;
  onClose: () => void;
  onReplaced: () => void;
}

type DrawState = "idle" | "drawing";

// The tracing wheel routes to its rule sets by category name.  These match
// the 11 files inside chart-tracing's `categories/` folder.  Selecting the
// correct one is what turns tuned=False into tuned=True and lets the tracer
// separate carpet families instead of merging them into one curve.
const CHART_CATEGORIES: { id: string; label: string; hint: string }[] = [
  { id: "",              label: "— auto —",       hint: "Let the tracer pick, or fall back to the folder's chart_name" },
  { id: "dual_carpet",   label: "Dual Carpet ⚙",  hint: "TWO independent curve families sharing one axis (custom Python handler, no wheel)" },
  { id: "mesh_chart",    label: "Mesh / Carpet",  hint: "Two crossing families (Runway Length & V1, accelerate-stop, etc.)" },
  { id: "wind",          label: "Wind correction", hint: "Wide short strip with wind curves over a REF LINE" },
  { id: "airport_oat",   label: "Airport OAT",    hint: "Tall panel, OAT along the bottom, pressure-altitude fan with a knee" },
  { id: "gross_weight",  label: "Gross Weight",   hint: "Straight parallel lines — takeoff gross weight fans" },
  { id: "climb_limits",  label: "Climb Limits",   hint: "Fan that turns downward — takeoff climb limits" },
  { id: "left_axis",     label: "Left-axis OAT",  hint: "Same OAT chart turned a quarter turn — OAT up the left" },
  { id: "bottom_axis",   label: "Bottom-axis fan", hint: "Generic fan entered from a heavy bottom axis, no y scale" },
  { id: "vertical_fan",  label: "Vertical fan",   hint: "Curves running near vertical — the column tracer normally misses these" },
  { id: "hatch_band",    label: "Hatch band",     hint: "Narrow band of short hatch strokes (Ice, Anti-ice, Clearway)" },
  { id: "defaults_checked", label: "Defaults (validated)", hint: "Charts where the generic defaults happen to be right" },
  { id: "generic",       label: "Generic fallback", hint: "Unknown chart type — untuned baseline" },
];

// The manual setup wizard walks the operator through four anchor clicks and
// then three text fields.  OCR was noisy and slow, so it is disabled and the
// operator does the labelling themselves — always identical output shape,
// always fast.
type WizardStep = null | "x_start" | "x_end" | "y_start" | "y_end" | "values";
const WIZARD_ORDER: Exclude<WizardStep, null>[] = ["x_start", "x_end", "y_start", "y_end", "values"];
const WIZARD_HINT: Record<Exclude<WizardStep, null>, string> = {
  x_start: "Step 1 / 5 — click where the X-axis STARTS (x₁, the left end)",
  x_end:   "Step 2 / 5 — click where the X-axis ENDS (xₙ, the right end)",
  y_start: "Step 3 / 5 — click where the Y-axis STARTS (y₁, the bottom)",
  y_end:   "Step 4 / 5 — click where the Y-axis ENDS (yₙ, the top)",
  values:  "Step 5 / 5 — type the tick values and curve names, then Save",
};

// When OCR returns no numeric ticks for an axis, we still show a default
// 0..10 range so the operator can see the axis structure and edit before
// saving.  This matches the spec: "if the OCR finds no number, mark the
// axis with a range starting from zero".
const DEFAULT_TICK_RANGE = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * Parse a tick-list input, expanding `...` into an arithmetic sequence.
 *
 *   "1, 2, 3, ..., 10"  →  [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
 *   "0, 5, ..., 25"     →  [0, 5, 10, 15, 20, 25]
 *   "10, 8, ..., 0"     →  [10, 8, 6, 4, 2, 0]
 *   "1, 2, 3"           →  [1, 2, 3]
 *
 * Step size is inferred from the two numbers immediately before the
 * ellipsis.  With only one number before it, step defaults to 1 (or -1 if
 * the value after the ellipsis is smaller).  Non-numeric tokens are
 * silently dropped so a stray letter doesn't kill the whole list.
 */
function expandTicks(input: string): number[] {
  const parts = input.split(/\s*,\s*/).map(p => p.trim()).filter(Boolean);
  const out: number[] = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p === "..." || p === "…") {
      if (out.length === 0 || i === parts.length - 1) continue;
      const start = out[out.length - 1];
      const nextRaw = Number(parts[i + 1]);
      if (isNaN(nextRaw)) continue;
      const step = out.length >= 2
        ? out[out.length - 1] - out[out.length - 2]
        : (nextRaw > start ? 1 : -1);
      if (step === 0) continue;
      let cur = start + step;
      while ((step > 0 && cur < nextRaw) || (step < 0 && cur > nextRaw)) {
        out.push(cur);
        cur += step;
      }
      // The nextRaw itself will land in the next loop iteration.
    } else {
      const n = Number(p);
      if (!isNaN(n)) out.push(n);
    }
  }
  return out;
}

export function CropReviewModal({ path, jobId, panel, onClose, onReplaced }: Props) {
  const [trace, setTrace] = useState<CropTrace | null>(null);
  const [ocr, setOcr] = useState<BatchOcr | null>(null);
  // "Try all tracers" — runs every registered chart-tracing handler on this
  // crop and lets the operator pick which overlay to hand off to WPD.
  const [allTracersOpen, setAllTracersOpen] = useState(false);

  // Editable OCR tick state
  const [xEdit, setXEdit] = useState("");
  const [yEdit, setYEdit] = useState("");
  // Curve labels split into two families: horizontal (→) and vertical (↑).
  // Charts with a single family populate only hEdit.
  const [hEdit, setHEdit] = useState("");
  const [vEdit, setVEdit] = useState("");
  const [ocrSaveMsg, setOcrSaveMsg] = useState<string | null>(null);
  const [showOcrEdit, setShowOcrEdit] = useState(false);

  // Crop image measurements for marker overlays
  const cropImgRef = useRef<HTMLImageElement | null>(null);
  const [cropRendered, setCropRendered] = useState<{ w: number; h: number } | null>(null);

  // Cache-bust counter — replacePanel writes to the same S3 key so the URL
  // stays identical; without a query-string tick the browser keeps showing
  // the previous crop.  Seeded with the mount timestamp so re-opening the
  // modal after a Save New Crop always fetches fresh bytes (previously
  // reset to 0 on close, and the browser served its cached copy of the
  // old crop even though S3 held the new one).  Bumped after
  // saveCorrection resolves for a live in-modal refresh.
  const [imageVersion, setImageVersion] = useState(() => Date.now());
  const bust = (url: string) =>
    `${url}${url.includes("?") ? "&" : "?"}v=${imageVersion}`;

  // Marker-edit mode: drag existing markers, click empty axis area to add.
  const [editMarkers, setEditMarkers] = useState(false);
  const [hoverPx, setHoverPx] = useState<{ x: number; y: number } | null>(null);
  const [dragMarker, setDragMarker] = useState<
    { axis: "x" | "y"; index: number } | null
  >(null);

  // Manual-setup wizard: null when idle, otherwise one of the ordered steps.
  const [wizardStep, setWizardStep] = useState<WizardStep>(null);
  // Anchor-mode placement: null when idle, or the anchor we're about to place.
  // Set by clicking one of the four "Set x₁ / xₙ / y₁ / yₙ" buttons; the next
  // click on the image lands as that specific anchor and mode returns to null.
  type AnchorId = "x_start" | "x_end" | "y_start" | "y_end";
  const [placingAnchor, setPlacingAnchor] = useState<AnchorId | null>(null);

  // Chart type — the tracer rule set to route to.  Persisted with the OCR
  // JSON so future traces of this panel keep the same choice.
  const [category, setCategory] = useState<string>("");
  useEffect(() => {
    setCategory(ocr?.category ?? "");
  }, [ocr?.category]);

  // AI-analyzer state inside the modal.  Runs the same endpoint the tile
  // button uses, then refreshes OCR + opens the edit form pre-filled with
  // whatever the LLM suggested so the operator can just glance-and-save.
  const [aiBusy, setAiBusy] = useState(false);
  const [aiInfo, setAiInfo] = useState<{
    model: string; inTok: number; outTok: number; cost: number;
    name: string | null; category: string | null;
  } | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);

  const askAiHere = async () => {
    setAiBusy(true);
    setAiError(null);
    try {
      const r = await api.batch.aiAnalyzePanel(path, jobId, panel.name);
      setAiInfo({
        model: r.model_name ?? "?",
        inTok: r.input_tokens,
        outTok: r.output_tokens,
        cost: r.cost_usd,
        name: r.name,
        category: r.category,
      });
      const fresh = await api.batch.getOcr(path, jobId, panel.name);
      setOcr(fresh);
      // Prefill the edit form with whatever the LLM returned so the
      // operator sees the suggestions immediately without an extra click.
      const xs = fresh?.x_ticks ?? [];
      const ys = fresh?.y_ticks ?? [];
      setXEdit((xs.length ? xs : DEFAULT_TICK_RANGE).join(", "));
      setYEdit((ys.length ? ys : DEFAULT_TICK_RANGE).join(", "));
      setHEdit((fresh?.h_labels ?? fresh?.line_labels ?? []).join(", "));
      setVEdit((fresh?.v_labels ?? []).join(", "));
      setShowOcrEdit(true);
      setOcrSaveMsg("✓ AI suggestions loaded — review + Save");
    } catch (e) {
      setAiError(e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(false);
    }
  };

  // WPD export snapshots — the operator can save several variations per crop
  // and download / delete each one from the modal.
  const [exports, setExports] = useState<
    { name: string; size: number; saved_at: string | null;
      uploaded?: boolean; source_filename?: string | null }[]
  >([]);
  const [newExportName, setNewExportName] = useState("");
  const [exportBusy, setExportBusy] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  const uploadInputRef = useRef<HTMLInputElement | null>(null);

  // Deskew — CSS-rotates the crop image + marker overlay so a tilted scan
  // can be levelled before placing anchors.  Clicks are inverse-rotated by
  // AxisOverlay so anchor coordinates stay in the ORIGINAL image frame,
  // which is what the tracer + WPD calibration downstream expect.  Not
  // persisted server-side yet — the rotation is a visual aid for anchor
  // placement, and the anchors themselves get saved in the un-rotated
  // frame so downstream stays consistent.
  const [skewDeg, setSkewDeg] = useState(0);

  const refreshExports = async () => {
    try {
      const r = await api.batch.listExports(path, jobId, panel.name);
      setExports(r.exports);
    } catch { /* silent */ }
  };
  useEffect(() => { void refreshExports(); }, [path, jobId, panel.name]);

  useEffect(() => {
    api.batch.tracePanelStatus(path, jobId, panel.name)
      .then(setTrace)
      .catch(() => {});
    api.batch.getOcr(path, jobId, panel.name)
      .then((r) => { if (r.status !== "none") setOcr(r); })
      .catch(() => {});
  }, [path, jobId, panel.name]);

  // OCR trigger removed per manual-only spec.  The TrOCR path was too slow
  // and too inaccurate — everything is now entered by hand via the wizard.
  // To re-enable, restore this function and the "Run OCR" button in the
  // Axes & Curves section.  api.batch.queueOcr is kept in the client so the
  // endpoint remains reachable for a future retrained model.

  const openOcrEdit = () => {
    // If OCR came back empty for an axis, prefill with the 0..10 default so
    // the operator has something to edit rather than a blank box.
    const xs = ocr?.x_ticks ?? [];
    const ys = ocr?.y_ticks ?? [];
    setXEdit((xs.length ? xs : DEFAULT_TICK_RANGE).join(", "));
    setYEdit((ys.length ? ys : DEFAULT_TICK_RANGE).join(", "));
    // Backward-compat: if the panel only has line_labels (older records),
    // treat them all as horizontal so nothing is lost.
    const h = ocr?.h_labels ?? (ocr?.v_labels ? [] : ocr?.line_labels ?? []);
    const v = ocr?.v_labels ?? [];
    setHEdit(h.join(", "));
    setVEdit(v.join(", "));
    setShowOcrEdit(true);
    setOcrSaveMsg(null);
  };

  // Persist an updated marker layout (values + positions) to the backend.
  // Called after every drag-drop or click-to-add so the UI stays in sync
  // with what will be reloaded when the modal is re-opened.
  const commitOcrLayout = async (next: BatchOcr) => {
    try {
      await api.batch.correctOcr(path, jobId, panel.name,
        next.x_ticks ?? [], next.y_ticks ?? [], next.line_labels ?? [],
        {
          xPositions: next.x_positions,
          yPositions: next.y_positions,
          xPositionsPy: next.x_positions_py,
          yPositionsPx: next.y_positions_px,
          linePositions: next.line_positions,
          hLabels: next.h_labels,
          vLabels: next.v_labels,
          category: next.category ?? category ?? undefined,
        });
      setOcrSaveMsg("✓ Markers saved");
    } catch (e) {
      setOcrSaveMsg(`✗ ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Convert a tick-index (into x_ticks/y_ticks) to the matching index in the
  // raw positions array.  When the axis is in two-anchor mode (positions has
  // fewer elements than ticks), only the first and last ticks map to a
  // position — middle ticks are placed by interpolation and aren't draggable.
  const tickToPosIndex = (ticks: number[], positions: number[], tickIndex: number): number => {
    if (positions.length === ticks.length) return tickIndex;
    if (tickIndex === 0) return 0;
    if (tickIndex === ticks.length - 1) return positions.length - 1;
    return -1;
  };

  // Move an anchor.  In 2-anchor mode the caller may pass a large tick index
  // (e.g. index 9 for x_ticks = [1..10]); tickToPosIndex maps that back to the
  // real position 1.
  const moveMarker = (axis: "x" | "y", tickIndex: number, newPx: number) => {
    if (!ocr) return;
    const ticks = axis === "x" ? ocr.x_ticks ?? [] : ocr.y_ticks ?? [];
    const positions = [...(axis === "x" ? ocr.x_positions ?? [] : ocr.y_positions ?? [])];
    const posIdx = tickToPosIndex(ticks, positions, tickIndex);
    if (posIdx < 0) return;
    positions[posIdx] = Math.max(0, Math.round(newPx));
    const next: BatchOcr = axis === "x"
      ? { ...ocr, x_positions: positions, corrected: true }
      : { ...ocr, y_positions: positions, corrected: true };
    setOcr(next);
    void commitOcrLayout(next);
  };

  // Kick off the manual wizard from scratch: clear any prior anchors and
  // put the operator at step 1.  Enter "editMarkers" so subsequent clicks
  // register as wizard clicks and the loupe is shown.
  const startWizard = () => {
    setOcr({
      status: "done",
      x_ticks: [],
      y_ticks: [],
      line_labels: [],
      x_positions: [],
      y_positions: [],
      corrected: false,
    });
    setEditMarkers(true);
    setWizardStep("x_start");
    setOcrSaveMsg(null);
  };

  // Wipe everything the wizard set — both locally and on the server — so the
  // operator can start over on a botched calibration.  Kept separate from
  // startWizard because sometimes the operator just wants to clear without
  // immediately re-clicking the first anchor.
  const clearAxes = async () => {
    if (!window.confirm("Clear all wizard axes and curve names? Trace data is not touched.")) return;
    try {
      await api.batch.correctOcr(path, jobId, panel.name, [], [], [],
        { xPositions: [], yPositions: [], hLabels: [], vLabels: [] });
      setOcr({
        status: "done",
        x_ticks: [], y_ticks: [], line_labels: [],
        x_positions: [], y_positions: [],
        h_labels: [], v_labels: [],
        corrected: false,
      });
      setWizardStep(null);
      setEditMarkers(false);
      setOcrSaveMsg("✓ Cleared — start the wizard again");
    } catch (e) {
      setOcrSaveMsg(`✗ ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Advance to the next wizard step, or leave it running / finish.
  const advanceWizard = () => {
    if (!wizardStep) return;
    const idx = WIZARD_ORDER.indexOf(wizardStep);
    const next = WIZARD_ORDER[idx + 1] ?? null;
    setWizardStep(next);
    if (next === "values") {
      // Seed the edit form so the operator can type values immediately.
      openOcrEdit();
    }
  };

  // Snap the click position to an existing anchor when the operator lands
  // within `SNAP_PX` of one.  A carpet chart's y-axis usually shares its
  // starting x-coordinate with the x-axis's start; snapping means the two
  // Place a single named anchor at the clicked px/py.  Called when the
  // operator has picked an anchor mode via the four labelled buttons.  Fills
  // in default tick values (0 / 10) so the axis has something to render; the
  // operator can refine values in the ✏ Edit form later.
  const placeAnchor = (which: AnchorId, px: number, py: number) => {
    if (!ocr) return;
    let next: BatchOcr;
    // Helper: extend an array so its length matches ticks — used when the
    // parallel py/px array wasn't populated before (older records or the
    // first click on this axis).
    const pad = (arr: number[], n: number, fill: number): number[] => {
      const out = [...arr];
      while (out.length < n) out.push(fill);
      return out.slice(0, n);
    };

    if (which === "x_start") {
      const xTicks = [...(ocr.x_ticks ?? [])];
      const xPos = [...(ocr.x_positions ?? [])];
      const xPy = [...(ocr.x_positions_py ?? [])];
      if (xTicks.length === 0) xTicks.push(0);
      if (xPos.length === 0) xPos.push(px); else xPos[0] = px;
      if (xPy.length === 0) xPy.push(py); else xPy[0] = py;
      next = { ...ocr, x_ticks: xTicks, x_positions: xPos,
               x_positions_py: pad(xPy, xTicks.length, py), corrected: true };
    } else if (which === "x_end") {
      const xTicks = [...(ocr.x_ticks ?? [])];
      const xPos = [...(ocr.x_positions ?? [])];
      const xPy = [...(ocr.x_positions_py ?? [])];
      if (xTicks.length === 0) { xTicks.push(0, 10); }
      else if (xTicks.length === 1) { xTicks.push(10); }
      if (xPos.length === 0) { xPos.push(0, px); }
      else if (xPos.length === 1) { xPos.push(px); }
      else { xPos[xPos.length - 1] = px; }
      if (xPy.length === 0) { xPy.push(py, py); }
      else if (xPy.length === 1) { xPy.push(py); }
      else { xPy[xPy.length - 1] = py; }
      next = { ...ocr, x_ticks: xTicks, x_positions: xPos,
               x_positions_py: pad(xPy, xTicks.length, py), corrected: true };
    } else if (which === "y_start") {
      const yTicks = [...(ocr.y_ticks ?? [])];
      const yPos = [...(ocr.y_positions ?? [])];
      const yPx = [...(ocr.y_positions_px ?? [])];
      if (yTicks.length === 0) yTicks.push(0);
      if (yPos.length === 0) yPos.push(py); else yPos[0] = py;
      if (yPx.length === 0) yPx.push(px); else yPx[0] = px;
      next = { ...ocr, y_ticks: yTicks, y_positions: yPos,
               y_positions_px: pad(yPx, yTicks.length, px), corrected: true };
    } else {
      // y_end
      const yTicks = [...(ocr.y_ticks ?? [])];
      const yPos = [...(ocr.y_positions ?? [])];
      const yPx = [...(ocr.y_positions_px ?? [])];
      if (yTicks.length === 0) { yTicks.push(0, 10); }
      else if (yTicks.length === 1) { yTicks.push(10); }
      if (yPos.length === 0) { yPos.push(0, py); }
      else if (yPos.length === 1) { yPos.push(py); }
      else { yPos[yPos.length - 1] = py; }
      if (yPx.length === 0) { yPx.push(px, px); }
      else if (yPx.length === 1) { yPx.push(px); }
      else { yPx[yPx.length - 1] = px; }
      next = { ...ocr, y_ticks: yTicks, y_positions: yPos,
               y_positions_px: pad(yPx, yTicks.length, px), corrected: true };
    }
    setOcr(next);
    setPlacingAnchor(null);
    void commitOcrLayout(next);
  };

  // Handle a wizard click on the image.  Each of the first four steps stores
  // a single pixel coordinate as the corresponding anchor and advances.
  const handleWizardClick = (px: number, py: number) => {
    if (!ocr) return;
    // Every wizard click stores BOTH pixel coordinates — the position on
    // its own axis (px for X, py for Y) AND the "other" coordinate
    // (x_positions_py for X-anchor clicks, y_positions_px for Y-anchor
    // clicks).  Losing the "other" coord was the bug behind "I click X1
    // near the top and it jumps to the bottom": the calibration builder
    // defaulted the missing py to y_lo, which snapped the marker down.
    if (wizardStep === "x_start") {
      setOcr({ ...ocr,
        x_ticks: [0], x_positions: [px], x_positions_py: [py],
      });
      advanceWizard();
    } else if (wizardStep === "x_end") {
      const xTicks = [...(ocr.x_ticks?.length ? ocr.x_ticks : [0]), 10];
      const xPos = [...(ocr.x_positions?.length ? ocr.x_positions : [0]), px];
      const xPy = [...(ocr.x_positions_py?.length ? ocr.x_positions_py : [py]), py];
      setOcr({ ...ocr,
        x_ticks: xTicks, x_positions: xPos, x_positions_py: xPy,
      });
      advanceWizard();
    } else if (wizardStep === "y_start") {
      setOcr({ ...ocr,
        y_ticks: [0], y_positions: [py], y_positions_px: [px],
      });
      advanceWizard();
    } else if (wizardStep === "y_end") {
      const yTicks = [...(ocr.y_ticks?.length ? ocr.y_ticks : [0]), 10];
      const yPos = [...(ocr.y_positions?.length ? ocr.y_positions : [0]), py];
      const yPx = [...(ocr.y_positions_px?.length ? ocr.y_positions_px : [px]), px];
      setOcr({ ...ocr,
        y_ticks: yTicks, y_positions: yPos, y_positions_px: yPx,
      });
      advanceWizard();
    }
  };

  // Set (or replace) the start/end anchor of an axis.  The spec collapses the
  // full tick list down to two anchors per axis — x₁/xₙ and y₁/yₙ — and
  // clicks land on whichever end of the axis strip the operator hit.  For a
  // fresh axis with no values yet, we seed value=0 for the start and value=10
  // for the end so the user has something to edit in the ✏ form.
  const setAnchor = (axis: "x" | "y", which: "start" | "end", newPx: number) => {
    if (!ocr) return;
    const values = [...(axis === "x" ? ocr.x_ticks ?? [] : ocr.y_ticks ?? [])];
    const positions = [...(axis === "x" ? ocr.x_positions ?? [] : ocr.y_positions ?? [])];
    const px = Math.max(0, Math.round(newPx));
    if (which === "start") {
      if (positions.length === 0) { values.push(0); positions.push(px); }
      else                        { positions[0] = px; }
    } else {
      if (positions.length === 0) { values.push(0, 10); positions.push(px, px); }
      else if (positions.length === 1) { values.push(10); positions.push(px); }
      else                        { positions[positions.length - 1] = px; }
    }
    const next: BatchOcr = axis === "x"
      ? { ...ocr, x_ticks: values, x_positions: positions, corrected: true }
      : { ...ocr, y_ticks: values, y_positions: positions, corrected: true };
    setOcr(next);
    void commitOcrLayout(next);
  };

  // (resetOcr helper removed with the "wipe panel OCR on Save Export"
  // regression — Clear axes is now the only path that empties the
  // working state, and it lives in CropActions on the tile.)

  const saveExport = async () => {
    if (!newExportName.trim()) {
      setExportMsg("✗ Enter a name first");
      return;
    }
    setExportBusy(true);
    setExportMsg(null);
    try {
      // Commit whatever's currently in the edit form BEFORE snapshotting —
      // the edit-form state is local to the modal, so if the operator
      // typed curve names but never clicked "Save correction", the backend
      // OCR JSON still has the old values and the export would freeze the
      // wrong state.  Sending the current form values through correctOcr
      // makes Save Export always match what the operator sees.
      const h = expandTicks(hEdit);
      const v = expandTicks(vEdit);
      await api.batch.correctOcr(path, jobId, panel.name,
        expandTicks(xEdit) .length ? expandTicks(xEdit) : (ocr?.x_ticks ?? []),
        expandTicks(yEdit).length ? expandTicks(yEdit) : (ocr?.y_ticks ?? []),
        [...h, ...v],
        {
          xPositions: ocr?.x_positions,
          yPositions: ocr?.y_positions,
          xPositionsPy: ocr?.x_positions_py,
          yPositionsPx: ocr?.y_positions_px,
          hLabels: h.length ? h : (ocr?.h_labels ?? []),
          vLabels: v.length ? v : (ocr?.v_labels ?? []),
          category: category || undefined,
        });

      await api.batch.saveExport(path, jobId, panel.name, newExportName.trim());
      await refreshExports();
      // Do NOT wipe panel OCR here.  Snapshotting an export is orthogonal
      // to the panel's working state — resetting it was the root cause of
      // the "Retrace after Save Export drops to 1 curve" bug (Panel OCR
      // is what task_trace_crop reads; when it's empty the handler falls
      // back to the wheel and dual_carpet has no wheel rule).  Clear axes
      // is still available as an explicit reset when the operator wants
      // one.
      setExportMsg(`✓ Saved "${newExportName.trim()}" — panel state preserved`);
      setNewExportName("");
    } catch (e) {
      setExportMsg(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setExportBusy(false);
    }
  };

  // Load a saved export's calibration back into the modal — write it to the
  // current OCR so the AxisOverlay + wizard form all reflect it — then let
  // the operator edit and Save again (same name overwrites, different name
  // creates a new variant).
  const loadExport = async (name: string) => {
    setExportMsg(null);
    try {
      const snap = await api.batch.loadExportOcr(path, jobId, panel.name, name);
      await api.batch.correctOcr(path, jobId, panel.name,
        snap.x_ticks ?? [], snap.y_ticks ?? [], snap.line_labels ?? [],
        {
          xPositions: snap.x_positions,
          yPositions: snap.y_positions,
          hLabels: snap.h_labels,
          vLabels: snap.v_labels,
        });
      setOcr({ ...snap, status: "done", corrected: true });
      setNewExportName(name);
      setWizardStep(null);
      setPlacingAnchor(null);
      setExportMsg(`✓ Loaded "${name}" — edit then Save (same name = overwrite)`);
    } catch (e) {
      setExportMsg(`✗ ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const deleteExport = async (name: string) => {
    if (!window.confirm(`Delete export "${name}"?`)) return;
    try {
      await api.batch.deleteExport(path, jobId, panel.name, name);
      await refreshExports();
    } catch (e) {
      setExportMsg(`✗ ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const hasCalibration = (ocr?.x_positions?.length ?? 0) >= 2 &&
                        (ocr?.y_positions?.length ?? 0) >= 2;

  const suggestLabelsFromX = (which: "first" | "last") => {
    // Spec: curve names come from a number on the line, or from the first / last
    // X-axis tick.  Fills the horizontal-family input, which is the common case
    // for a single-family chart.  The vertical field stays untouched.
    const xs = ocr?.x_ticks?.length ? ocr.x_ticks : DEFAULT_TICK_RANGE;
    const n = trace?.curve_count && trace.curve_count > 0
      ? trace.curve_count
      : xs.length;
    const picked = which === "first" ? xs.slice(0, n) : xs.slice(-n);
    setHEdit(picked.join(", "));
  };

  const saveOcrCorrection = async () => {
    try {
      const h = expandTicks(hEdit);
      const v = expandTicks(vEdit);
      // line_labels stays as the flat union of both families so anything
      // that reads the old field still gets the whole set.
      const combined = [...h, ...v];
      await api.batch.correctOcr(path, jobId, panel.name,
        expandTicks(xEdit), expandTicks(yEdit), combined,
        {
          // Preserve the wizard-clicked pixel positions so a subsequent
          // reload of the modal keeps everything the operator set up.
          xPositions: ocr?.x_positions,
          yPositions: ocr?.y_positions,
          xPositionsPy: ocr?.x_positions_py,
          yPositionsPx: ocr?.y_positions_px,
          hLabels: h,
          vLabels: v,
          category: category || undefined,
        },
      );
      const updated = await api.batch.getOcr(path, jobId, panel.name);
      setOcr(updated);
      setShowOcrEdit(false);
      setWizardStep(null);
      setOcrSaveMsg("✓ Saved");
    } catch (e) {
      setOcrSaveMsg(`✗ ${e instanceof Error ? e.message : String(e)}`);
    }
  };
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [drawState, setDrawState] = useState<DrawState>("idle");
  const [startPt, setStartPt] = useState<{ x: number; y: number } | null>(null);
  const [rect, setRect] = useState<[number, number, number, number] | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  const hasSource = panel.page_num != null;
  const sourceUrl = hasSource
    ? api.batch.sourcePageUrl(path, jobId, panel.page_num!)
    : null;

  // Seed the working rect from the panel's existing bbox when the operator
  // enters edit mode, so they're resizing rather than drawing from scratch.
  useEffect(() => {
    if (editMode && !rect && panel.bbox) setRect(panel.bbox);
    if (!editMode) setRect(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode]);

  type Handle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "inside" | "new";
  const [hoverHandle, setHoverHandle] = useState<Handle | null>(null);
  const [dragStart, setDragStart] = useState<{
    handle: Handle;
    mouse: { x: number; y: number };
    startRect: [number, number, number, number];
  } | null>(null);
  const CURSOR: Record<Handle, string> = {
    nw: "nwse-resize", se: "nwse-resize",
    ne: "nesw-resize", sw: "nesw-resize",
    n: "ns-resize", s: "ns-resize",
    e: "ew-resize", w: "ew-resize",
    inside: "move",
    new: "crosshair",
  };

  // Draw source page + highlight + handles on canvas.
  const redraw = useCallback((newRect?: [number, number, number, number] | null) => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgLoaded) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.drawImage(img, 0, 0);

    const highlight = newRect ?? rect ?? panel.bbox ?? null;
    if (highlight) {
      const [x1, y1, x2, y2] = highlight;
      ctx.strokeStyle = "#f97316";
      ctx.lineWidth = 3;
      ctx.strokeRect(x1, y1, x2 - x1, y2 - y1);
      ctx.fillStyle = "rgba(249,115,22,0.12)";
      ctx.fillRect(x1, y1, x2 - x1, y2 - y1);

      // Corner + edge-midpoint handles for resize.  Only visible in edit mode
      // so the same canvas can show a passive highlight when reviewing.
      if (editMode) {
        const H = 10; // half-size of each handle square
        const points: [number, number][] = [
          [x1, y1], [x2, y1], [x1, y2], [x2, y2],
          [(x1 + x2) / 2, y1], [(x1 + x2) / 2, y2],
          [x1, (y1 + y2) / 2], [x2, (y1 + y2) / 2],
        ];
        for (const [cx, cy] of points) {
          ctx.fillStyle = "#fff";
          ctx.strokeStyle = "#f97316";
          ctx.lineWidth = 2;
          ctx.fillRect(cx - H, cy - H, H * 2, H * 2);
          ctx.strokeRect(cx - H, cy - H, H * 2, H * 2);
        }
      }
    }
  }, [imgLoaded, rect, panel.bbox, editMode]);

  useEffect(() => { redraw(); }, [redraw]);

  // Client coordinates → natural image pixels.
  const toCanvas = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    const scaleX = canvas.width / r.width;
    const scaleY = canvas.height / r.height;
    return {
      x: Math.round((e.clientX - r.left) * scaleX),
      y: Math.round((e.clientY - r.top) * scaleY),
    };
  };

  // Hit test: which handle (if any) is the cursor over?  Padding scales with
  // how zoomed-out the canvas is so a click on a handle registers even when
  // the source page is rendered small.
  const hitHandle = (pt: { x: number; y: number }): Handle => {
    if (!rect) return "new";
    const canvas = canvasRef.current;
    const scale = canvas ? canvas.width / canvas.getBoundingClientRect().width : 1;
    const PAD = 15 * scale;
    const [x1, y1, x2, y2] = rect;
    const nearL = Math.abs(pt.x - x1) < PAD;
    const nearR = Math.abs(pt.x - x2) < PAD;
    const nearT = Math.abs(pt.y - y1) < PAD;
    const nearB = Math.abs(pt.y - y2) < PAD;
    const inRangeX = pt.x >= x1 - PAD && pt.x <= x2 + PAD;
    const inRangeY = pt.y >= y1 - PAD && pt.y <= y2 + PAD;
    if (nearL && nearT) return "nw";
    if (nearR && nearT) return "ne";
    if (nearL && nearB) return "sw";
    if (nearR && nearB) return "se";
    if (nearT && inRangeX) return "n";
    if (nearB && inRangeX) return "s";
    if (nearL && inRangeY) return "w";
    if (nearR && inRangeY) return "e";
    if (pt.x > x1 && pt.x < x2 && pt.y > y1 && pt.y < y2) return "inside";
    return "new";
  };

  const applyHandle = (
    handle: Handle,
    dx: number,
    dy: number,
    start: [number, number, number, number],
  ): [number, number, number, number] => {
    let [x1, y1, x2, y2] = start;
    if (handle === "inside") { x1 += dx; y1 += dy; x2 += dx; y2 += dy; }
    else {
      if (handle.includes("w")) x1 += dx;
      if (handle.includes("e")) x2 += dx;
      if (handle.includes("n")) y1 += dy;
      if (handle.includes("s")) y2 += dy;
    }
    return [Math.min(x1, x2), Math.min(y1, y2), Math.max(x1, x2), Math.max(y1, y2)];
  };

  const onMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!editMode) return;
    const pt = toCanvas(e);
    const handle = hitHandle(pt);
    if (handle === "new") {
      // Start a fresh draw when the operator clicks outside the current rect.
      setStartPt(pt);
      setDrawState("drawing");
      setRect(null);
      return;
    }
    if (rect) {
      setDragStart({ handle, mouse: pt, startRect: rect });
    }
  };

  const onMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!editMode) return;
    const pt = toCanvas(e);

    // Drawing a brand-new rect
    if (drawState === "drawing" && startPt) {
      const r: [number, number, number, number] = [startPt.x, startPt.y, pt.x, pt.y];
      setRect(r);
      redraw(r);
      return;
    }
    // Actively resizing / moving an existing rect
    if (dragStart) {
      const dx = pt.x - dragStart.mouse.x;
      const dy = pt.y - dragStart.mouse.y;
      const r = applyHandle(dragStart.handle, dx, dy, dragStart.startRect);
      setRect(r);
      redraw(r);
      return;
    }
    // Passive hover → update cursor
    setHoverHandle(hitHandle(pt));
  };

  const onMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (drawState === "drawing" && startPt) {
      const pt = toCanvas(e);
      const r: [number, number, number, number] = [
        Math.min(startPt.x, pt.x), Math.min(startPt.y, pt.y),
        Math.max(startPt.x, pt.x), Math.max(startPt.y, pt.y),
      ];
      setRect(r);
      setDrawState("idle");
      redraw(r);
    }
    setDragStart(null);
  };

  const saveCorrection = async () => {
    if (!rect || panel.page_num == null) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      // Persist the wizard state first so anchor pixels + tick values + curve
      // names survive the crop replacement.  The pixel positions were captured
      // against the OLD crop; that's fine — a second review pass can re-drag
      // them onto the new crop, and having them stored is strictly better
      // than losing the operator's work.
      if (ocr?.status === "done" &&
          ((ocr.x_ticks?.length ?? 0) > 0 || (ocr.y_ticks?.length ?? 0) > 0)) {
        await commitOcrLayout(ocr);
      }
      await api.batch.replacePanel(path, jobId, panel.name, rect, panel.page_num);
      setSaveMsg("✓ Crop saved and wizard state preserved");
      setEditMode(false);
      setImageVersion(v => v + 1); // force <img> reload
      onReplaced();
    } catch (e) {
      setSaveMsg(`✗ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-rdp-card border border-rdp-border rounded-xl shadow-2xl w-[96vw] max-w-[1700px] max-h-[96vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-rdp-border">
          <div>
            <p className="text-sm font-semibold text-slate-200">{panel.name}</p>
            {panel.page_num != null && (
              <p className="text-[11px] text-slate-500">
                Page {panel.page_num + 1}
                {panel.bbox && (
                  <span className="ml-2 text-slate-600">
                    bbox [{panel.bbox.join(", ")}]
                  </span>
                )}
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200 text-xl px-2">✕</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4 flex flex-col gap-4 min-h-0">
          {/* Top: Cropped panel — FULL WIDTH so axis markers are readable.
              Hidden while editing crop so the operator can see the source PDF
              as large as possible for pixel-precise handle drags. */}
          <div className={`flex flex-col gap-2 ${editMode ? "hidden" : ""}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <p className="text-xs font-medium text-slate-400">Cropped panel</p>
                <button
                  onClick={startWizard}
                  className="text-xs px-2 py-0.5 rounded border border-emerald-600 text-emerald-400 bg-emerald-600/10 hover:bg-emerald-600/20"
                  title="Walk through 4 clicks + tick values to fully calibrate this panel"
                >
                  🧙 Start Wizard
                </button>
                {(hasCalibration || (ocr?.x_ticks?.length ?? 0) > 0) && (
                  <button
                    onClick={() => void clearAxes()}
                    className="text-xs px-2 py-0.5 rounded border border-red-700 text-red-400 hover:bg-red-500/10"
                    title="Delete all wizard anchors + curve names for this panel"
                  >
                    🗑 Clear axes
                  </button>
                )}
                {ocr?.status === "done" && (
                  <button
                    onClick={() => setEditMarkers(v => !v)}
                    className={`text-xs px-2 py-0.5 rounded border ${
                      editMarkers
                        ? "border-orange-500 text-orange-400 bg-orange-500/10"
                        : "border-slate-600 text-slate-400 hover:text-slate-200"
                    }`}
                    title="Drag markers to reposition. Click empty bottom/left strip to add a new tick."
                  >
                    {editMarkers ? "🖐 Move markers — drag or click" : "🖐 Move Markers"}
                  </button>
                )}
              </div>
              {ocr?.status === "done" ? (
                <p className="text-[11px] text-slate-500">
                  <span className="text-orange-400 font-semibold">● axis ticks</span>
                  <span className="ml-3 text-cyan-400 font-semibold">● line labels</span>
                  <span className="ml-3 text-slate-600">— extremes shown as x₁, xₙ, y₁, yₙ</span>
                </p>
              ) : (
                <p className="text-[10px] text-slate-600">Start the wizard to set axes and curve names manually</p>
              )}
            </div>
            {wizardStep && (
              <div className="bg-emerald-600/15 border border-emerald-600/50 rounded px-3 py-2 flex items-center justify-between">
                <p className="text-sm text-emerald-300 font-medium">
                  {WIZARD_HINT[wizardStep]}
                </p>
                <button
                  onClick={() => setWizardStep(null)}
                  className="text-[10px] text-slate-400 hover:text-slate-200 border border-slate-600 rounded px-2 py-0.5"
                >
                  Skip wizard
                </button>
              </div>
            )}
            {/* Chart type — routes the tracer to the right rule set (mesh /
                wind / OAT / ...).  Selecting the wrong one, or leaving it
                on auto, is why a carpet chart's 20 parallel lines get
                merged into one curve.  Change is committed on select via
                commitOcrLayout so the very next trace uses it. */}
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-slate-500 flex-shrink-0">Chart type:</span>
              <select
                value={category}
                onChange={async (e) => {
                  const v = e.target.value;
                  setCategory(v);
                  if (ocr) {
                    const next = { ...ocr, category: v || undefined };
                    setOcr(next);
                    await commitOcrLayout(next);
                  }
                }}
                className="bg-rdp-bg border border-slate-600 rounded px-2 py-0.5 text-slate-200 text-[11px]"
                title={CHART_CATEGORIES.find(c => c.id === category)?.hint}
              >
                {CHART_CATEGORIES.map(c => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              {category && (
                <span className="text-emerald-400 text-[10px]" title="Tracer will use this rule set → tuned=True">
                  ✓ tuned rules
                </span>
              )}
              <button
                onClick={askAiHere}
                disabled={aiBusy}
                title="Ask the configured chart_analyze LLM to fill in name + category + axis ranges + curve labels — they'll pre-fill the edit form below."
                className="ml-auto text-[11px] font-medium text-purple-100 bg-purple-700/70 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-400 rounded px-2 py-0.5"
              >
                {aiBusy ? "🤖 Analysing…" : "🤖 Ask AI"}
              </button>
              {aiInfo && (
                <span
                  className="text-[9px] text-purple-300 truncate max-w-[180px]"
                  title={`Suggested: ${aiInfo.name ?? "?"} · category ${aiInfo.category ?? "?"} · ${aiInfo.model}`}
                >
                  ${aiInfo.cost.toFixed(4)} · {aiInfo.inTok + aiInfo.outTok} tok
                </span>
              )}
              {aiError && (
                <span className="text-[9px] text-red-400 truncate max-w-[180px]" title={aiError}>
                  🤖 ✗ {aiError}
                </span>
              )}
            </div>
            {/* Four independent anchor-place buttons.  Pick one, then click
                on the image — no forced order, and each button re-arms itself
                after a click so re-placing an anchor is one action, not a
                full wizard restart.

                Labels use LEFT/RIGHT/BOTTOM/TOP instead of x₁/xₙ/y₁/yₙ
                because the numeric names invite the operator to click the
                two Y anchors at the same y-coordinate (both at top, for
                example) — WPD's four-point calibration then collapses. */}
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="text-slate-500">Set axis corners:</span>
              {(
                [
                  { id: "x_start", label: "X-axis LEFT",
                    hint: "Click on the left end of the X-axis (x_min)",
                    px: ocr?.x_positions?.[0], v: ocr?.x_ticks?.[0] },
                  { id: "x_end", label: "X-axis RIGHT",
                    hint: "Click on the right end of the X-axis (x_max)",
                    px: ocr?.x_positions?.[(ocr.x_positions?.length ?? 0) - 1],
                    v: ocr?.x_ticks?.[(ocr.x_ticks?.length ?? 0) - 1] },
                  { id: "y_start", label: "Y-axis BOTTOM",
                    hint: "Click at the bottom of the Y-axis (y_min) — usually the lower-left corner",
                    px: ocr?.y_positions?.[0], v: ocr?.y_ticks?.[0] },
                  { id: "y_end", label: "Y-axis TOP",
                    hint: "Click at the top of the Y-axis (y_max) — usually the upper-left corner",
                    px: ocr?.y_positions?.[(ocr.y_positions?.length ?? 0) - 1],
                    v: ocr?.y_ticks?.[(ocr.y_ticks?.length ?? 0) - 1] },
                ] as const
              ).map(({ id, label, hint, px, v }) => {
                const active = placingAnchor === id;
                const isSet = px != null && (ocr?.x_positions?.length ?? 0) + (ocr?.y_positions?.length ?? 0) > 0;
                return (
                  <button
                    key={id}
                    onClick={() => setPlacingAnchor(active ? null : id)}
                    className={`px-2 py-0.5 rounded border ${
                      active
                        ? "border-amber-500 text-amber-400 bg-amber-500/15 animate-pulse"
                        : isSet && px != null
                          ? "border-emerald-600/60 text-emerald-400 hover:bg-emerald-600/10"
                          : "border-slate-600 text-slate-400 hover:text-slate-200"
                    }`}
                    title={active ? "Cancel — click again to disarm" : hint}
                  >
                    {active
                      ? `⟳ ${label} — click on image`
                      : `${label}${px != null ? ` (${px}px${v != null ? `, ${v}` : ""})` : ""}`}
                  </button>
                );
              })}
            </div>
            {placingAnchor && (
              <div className="bg-amber-600/15 border border-amber-600/50 rounded px-3 py-1.5 text-[11px] text-amber-300">
                Placing <span className="font-bold">{
                  placingAnchor === "x_start" ? "X-axis LEFT" :
                  placingAnchor === "x_end" ? "X-axis RIGHT" :
                  placingAnchor === "y_start" ? "Y-axis BOTTOM" : "Y-axis TOP"
                }</span> — click anywhere on the image (loupe follows the cursor for precision).
                {" "}<span className="text-amber-400">Y anchors need different heights</span> (one at the top of the axis, one at the bottom) — placing both at the same height collapses the calibration.
              </div>
            )}
            {/* Deskew slider — CSS-rotates the crop preview so the operator
                can level a tilted scan before dropping anchors.  Clicks are
                inverse-rotated inside AxisOverlay so anchor coords stay in
                the ORIGINAL image frame; the downstream WPD calibration
                sees only un-rotated coords. */}
            <div className="flex items-center gap-3 text-[11px] text-slate-400 px-1">
              <span className="whitespace-nowrap">🎯 Deskew</span>
              <input
                type="range"
                min={-30}
                max={30}
                step={0.1}
                value={skewDeg}
                onChange={(e) => setSkewDeg(Number(e.target.value))}
                className="flex-1"
                title="Rotate the preview to level a tilted scan.  Anchor clicks stay accurate on the original image."
              />
              <span className="font-mono text-slate-300 w-14 text-right">
                {skewDeg.toFixed(1)}°
              </span>
              <button
                onClick={() => setSkewDeg(0)}
                disabled={skewDeg === 0}
                className="text-[10px] px-1.5 py-0.5 rounded border border-slate-600 text-slate-400 hover:text-slate-200 disabled:opacity-40"
                title="Reset rotation to 0°"
              >
                Reset
              </button>
            </div>
            <div className="overflow-auto border border-rdp-border rounded bg-black/30 flex items-center justify-center p-2">
              <div
                className="relative inline-block max-w-full"
                style={{
                  transform: skewDeg ? `rotate(${-skewDeg}deg)` : undefined,
                  transformOrigin: "center center",
                  transition: "transform 120ms ease-out",
                }}
              >
                <img
                  ref={cropImgRef}
                  src={bust(panel.url)}
                  alt={panel.name}
                  className="max-w-full h-auto block select-none"
                  style={{ maxHeight: "55vh" }}
                  draggable={false}
                  onLoad={(e) => {
                    const t = e.currentTarget;
                    setCropRendered({ w: t.naturalWidth, h: t.naturalHeight });
                  }}
                />
                {(editMarkers || placingAnchor !== null) && hoverPx && cropRendered && (
                  <Loupe
                    src={bust(panel.url)}
                    imgW={cropRendered.w}
                    imgH={cropRendered.h}
                    hover={hoverPx}
                    zoom={4}
                    size={180}
                    anchors={ocr ? {
                      x: (ocr.x_positions ?? []).map((px, i, arr) =>
                        i === 0 || i === arr.length - 1 ? px : null).filter((v): v is number => v != null),
                      y: (ocr.y_positions ?? []).map((py, i, arr) =>
                        i === 0 || i === arr.length - 1 ? py : null).filter((v): v is number => v != null),
                    } : undefined}
                  />
                )}
                {ocr?.status === "done" && cropRendered && (
                  <AxisOverlay
                    ocr={ocr}
                    imgW={cropRendered.w}
                    imgH={cropRendered.h}
                    rotationDeg={skewDeg}
                    interactive={editMarkers || placingAnchor !== null}
                    dragMarker={dragMarker}
                    onStartDrag={(axis, index) => setDragMarker({ axis, index })}
                    onDrag={(px, py) => {
                      setHoverPx({ x: px, y: py });
                      if (dragMarker) {
                        // Live preview during drag.  In 2-anchor mode the
                        // tick index (dragMarker.index) is usually 0 or
                        // ticks.length-1, but the position array is only
                        // 2 wide — tickToPosIndex bridges the two.
                        if (dragMarker.axis === "x" && ocr.x_positions) {
                          const posIdx = tickToPosIndex(ocr.x_ticks ?? [], ocr.x_positions, dragMarker.index);
                          if (posIdx < 0) return;
                          const next = [...ocr.x_positions];
                          next[posIdx] = Math.max(0, Math.round(px));
                          setOcr({ ...ocr, x_positions: next });
                        } else if (dragMarker.axis === "y" && ocr.y_positions) {
                          const posIdx = tickToPosIndex(ocr.y_ticks ?? [], ocr.y_positions, dragMarker.index);
                          if (posIdx < 0) return;
                          const next = [...ocr.y_positions];
                          next[posIdx] = Math.max(0, Math.round(py));
                          setOcr({ ...ocr, y_positions: next });
                        }
                      }
                    }}
                    onEndDrag={(px, py) => {
                      if (dragMarker) {
                        moveMarker(dragMarker.axis, dragMarker.index,
                                   dragMarker.axis === "x" ? px : py);
                        setDragMarker(null);
                      }
                    }}
                    wizardActive={(wizardStep !== null && wizardStep !== "values") || placingAnchor !== null}
                    onImageClick={(px, py) => {
                      // Priority 1 — anchor-mode placement (the four "Set x₁ /
                      // xₙ / y₁ / yₙ" buttons).  Any click on the image lands
                      // as the chosen anchor and exits the mode.
                      if (placingAnchor) {
                        placeAnchor(placingAnchor, px, py);
                        return;
                      }
                      // Priority 2 — sequential wizard.
                      if (wizardStep && wizardStep !== "values") {
                        handleWizardClick(px, py);
                        return;
                      }
                      // Priority 3 — plain edit-markers: strip-side rules.
                      if (!cropRendered) return;
                      const inBottom = py > cropRendered.h * 0.8;
                      const inLeft = px < cropRendered.w * 0.2;
                      if (inBottom) {
                        setAnchor("x", px < cropRendered.w / 2 ? "start" : "end", px);
                      } else if (inLeft) {
                        setAnchor("y", py < cropRendered.h / 2 ? "end" : "start", py);
                      }
                    }}
                    onHover={(pt) => setHoverPx(pt)}
                  />
                )}
              </div>
            </div>
            {(editMarkers || placingAnchor !== null) && (
              <div className="flex items-center justify-between text-[11px] font-mono">
                <div className="text-slate-400">
                  {hoverPx ? (
                    <>
                      <span className="text-orange-400 font-semibold">px = ({hoverPx.x}, {hoverPx.y})</span>
                      {cropRendered && (
                        <span className="ml-3 text-slate-500">
                          / {cropRendered.w}×{cropRendered.h}
                        </span>
                      )}
                      {dragMarker && (
                        <span className="ml-3 text-amber-400">
                          ⇢ dragging {dragMarker.axis}[{dragMarker.index}]
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-600">Hover the image to see px position…</span>
                  )}
                </div>
                <div className="text-slate-500 text-[10px]">
                  drag any orange dot · click bottom-left → x₁ · bottom-right → xₙ · left-top → yₙ · left-bottom → y₁
                </div>
              </div>
            )}
          </div>

          {/* Bottom: source page (left) + OCR/trace info (right).  In edit
              mode the source PDF is promoted to a single full-width column
              so an operator can drag handles with pixel precision. */}
          <div className={`grid gap-4 min-h-0 ${editMode ? "grid-cols-1" : "grid-cols-2"}`}>
          {/* Left: source page */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium text-slate-400">Original PDF page</p>
              {hasSource && (
                <button
                  onClick={() => { setEditMode(v => !v); setRect(null); }}
                  className={`text-xs px-2 py-0.5 rounded border ${
                    editMode
                      ? "border-orange-500 text-orange-400 bg-orange-500/10"
                      : "border-slate-600 text-slate-400 hover:text-slate-200"
                  }`}
                >
                  {editMode ? "✏ Editing — drag corners / edges to resize" : "✏ Edit Crop"}
                </button>
              )}
            </div>
            {sourceUrl ? (
              <div
                className="relative overflow-auto border border-rdp-border rounded bg-black/30 min-h-0"
                style={{ maxHeight: editMode ? "82vh" : "45vh" }}
              >
                {/* Hidden img used to load pixels */}
                <img
                  ref={(el) => {
                    imgRef.current = el;
                    if (el) {
                      el.onload = () => { setImgLoaded(true); };
                      if (el.complete) setImgLoaded(true);
                    }
                  }}
                  src={sourceUrl}
                  alt="source page"
                  className="hidden"
                />
                <canvas
                  ref={canvasRef}
                  className="w-full h-auto"
                  style={{
                    cursor: !editMode
                      ? "default"
                      : dragStart
                        ? CURSOR[dragStart.handle]
                        : hoverHandle
                          ? CURSOR[hoverHandle]
                          : "crosshair",
                  }}
                  onMouseDown={onMouseDown}
                  onMouseMove={onMouseMove}
                  onMouseUp={onMouseUp}
                  onMouseLeave={() => setHoverHandle(null)}
                />
                {!imgLoaded && (
                  <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-sm">
                    Loading…
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center text-slate-600 text-sm border border-rdp-border rounded" style={{ minHeight: "20vh" }}>
                No source page info
              </div>
            )}
          </div>

          {/* Right: OCR + trace panels — hidden while editing crop so the
              source PDF can span the full modal width. */}
          <div className={`flex flex-col gap-2 overflow-auto ${editMode ? "hidden" : ""}`} style={{ maxHeight: "45vh" }}>

            {/* Axes & Curves section — manually populated via the wizard.
                OCR path is commented-out per operator request (too slow, too
                inaccurate); everything below is entered by hand and saved
                to _ocr/<file>.json for reuse. */}
            <div className="bg-rdp-hover/50 border border-rdp-border rounded p-3 text-[11px]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-400 font-medium">Axes &amp; Curves (manual)</p>
                <div className="flex items-center gap-2">
                  {ocr?.status === "done" && !showOcrEdit && (
                    <button onClick={openOcrEdit}
                      className="text-slate-500 hover:text-slate-200 text-[10px] border border-slate-700 rounded px-1.5 py-0.5">
                      ✏ Edit
                    </button>
                  )}
                  {ocr?.corrected && (
                    <span className="text-emerald-400 text-[10px]">✓ saved</span>
                  )}
                  {/*
                    OCR button intentionally disabled per manual-only spec.
                    Re-enable by uncommenting when the TrOCR model is retrained.
                    <button onClick={runOcr} disabled={ocrBusy || ocr?.status === "running"}>
                      {ocr?.status === "running" ? "Running…" : ocrBusy ? "Queuing…" : ocr?.status === "done" ? "↻ Re-run OCR" : "▶ Run OCR"}
                    </button>
                  */}
                </div>
              </div>

              {!ocr || ocr.status === "none" ? (
                <p className="text-slate-600">Nothing set yet — click 🧙 Start Wizard above.</p>
              ) : ocr.status === "running" ? (
                <p className="text-slate-500">● …</p>
              ) : ocr.status === "failed" ? (
                <p className="text-red-400">✗ {ocr.error}</p>
              ) : showOcrEdit ? (
                <div className="space-y-2">
                  <p className="text-[10px] text-slate-500">
                    Use <span className="text-slate-300 font-mono">1, 2, 3, ..., 10</span> to auto-fill a range.
                    Step size comes from the two numbers before <span className="font-mono">...</span>.
                    Split curves into two families:{" "}
                    <span className="text-orange-400">→ horizontal</span> traverse X,{" "}
                    <span className="text-cyan-400">↑ vertical</span> traverse Y.
                  </p>
                  {[
                    { label: "X ticks (L→R)", value: xEdit, set: setXEdit, canSuggest: false },
                    { label: "Y ticks (B→T)", value: yEdit, set: setYEdit, canSuggest: false },
                    { label: "→ H-curves", value: hEdit, set: setHEdit, canSuggest: true },
                    { label: "↑ V-curves", value: vEdit, set: setVEdit, canSuggest: false },
                  ].map(({ label, value, set, canSuggest }) => {
                    const expanded = expandTicks(value);
                    const changed = value.includes("...") || value.includes("…");
                    return (
                      <div key={label} className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500 w-20 flex-shrink-0">{label}</span>
                          <input
                            value={value}
                            onChange={(e) => set(e.target.value)}
                            className="flex-1 bg-rdp-bg border border-slate-600 rounded px-2 py-0.5 text-slate-200 text-[11px]"
                            placeholder="e.g. 1, 2, ..., 10"
                          />
                          {canSuggest && (
                            <>
                              <button
                                type="button"
                                onClick={() => suggestLabelsFromX("first")}
                                title="Use X-tick values from the left as line names"
                                className="text-[9px] px-1.5 py-0.5 rounded border border-slate-600 text-slate-400 hover:text-slate-200"
                              >
                                ← first x
                              </button>
                              <button
                                type="button"
                                onClick={() => suggestLabelsFromX("last")}
                                title="Use X-tick values from the right as line names"
                                className="text-[9px] px-1.5 py-0.5 rounded border border-slate-600 text-slate-400 hover:text-slate-200"
                              >
                                last x →
                              </button>
                            </>
                          )}
                        </div>
                        {changed && expanded.length > 0 && (
                          <div className="ml-[5.5rem] text-[10px] text-emerald-400">
                            → {expanded.length} values: {expanded.join(", ")}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="flex gap-2 pt-1">
                    <button onClick={saveOcrCorrection}
                      className="text-[10px] px-2 py-1 rounded bg-orange-600 hover:bg-orange-500 text-white">
                      Save correction
                    </button>
                    <button onClick={() => setShowOcrEdit(false)}
                      className="text-[10px] px-2 py-1 rounded border border-slate-600 text-slate-400">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-slate-500">
                  <span>X ticks (x₁ … xₙ)</span>
                  <span className="col-span-2">
                    {(ocr.x_ticks ?? []).length > 0 ? (
                      <span className="text-slate-300">
                        <span className="text-orange-400 font-semibold">x₁={ocr.x_ticks![0]}</span>
                        {ocr.x_ticks!.length > 2 && <span>, {ocr.x_ticks!.slice(1, -1).join(", ")}</span>}
                        {ocr.x_ticks!.length > 1 && (
                          <span>, <span className="text-orange-400 font-semibold">xₙ={ocr.x_ticks![ocr.x_ticks!.length - 1]}</span></span>
                        )}
                      </span>
                    ) : (
                      <span className="text-amber-400">
                        {DEFAULT_TICK_RANGE.join(", ")}{" "}
                        <span className="text-[9px] opacity-70">(default 0..10 — click ✏ Edit)</span>
                      </span>
                    )}
                  </span>
                  <span>Y ticks (y₁ … yₙ)</span>
                  <span className="col-span-2">
                    {(ocr.y_ticks ?? []).length > 0 ? (
                      <span className="text-slate-300">
                        <span className="text-orange-400 font-semibold">y₁={ocr.y_ticks![0]}</span>
                        {ocr.y_ticks!.length > 2 && <span>, {ocr.y_ticks!.slice(1, -1).join(", ")}</span>}
                        {ocr.y_ticks!.length > 1 && (
                          <span>, <span className="text-orange-400 font-semibold">yₙ={ocr.y_ticks![ocr.y_ticks!.length - 1]}</span></span>
                        )}
                      </span>
                    ) : (
                      <span className="text-amber-400">
                        {DEFAULT_TICK_RANGE.join(", ")}{" "}
                        <span className="text-[9px] opacity-70">(default 0..10 — click ✏ Edit)</span>
                      </span>
                    )}
                  </span>
                  <span>→ H-curves</span>
                  <span className="col-span-2">
                    {(() => {
                      const h = ocr.h_labels ?? (ocr.v_labels ? [] : ocr.line_labels ?? []);
                      return h.length > 0
                        ? <span className="text-orange-300">{h.join(", ")}</span>
                        : <span className="text-amber-400 text-[10px]">none — Edit ✏ then click "first x" / "last x →"</span>;
                    })()}
                  </span>
                  <span>↑ V-curves</span>
                  <span className="col-span-2">
                    {(ocr.v_labels ?? []).length > 0 ? (
                      <span className="text-cyan-300">{ocr.v_labels!.join(", ")}</span>
                    ) : (
                      <span className="text-slate-600 text-[10px]">none</span>
                    )}
                  </span>
                  <span>Confidence</span>
                  <span className={`col-span-2 ${(ocr.confidence ?? 0) > 0.7 ? "text-emerald-400" : "text-amber-400"}`}>
                    {((ocr.confidence ?? 0) * 100).toFixed(0)}%
                  </span>
                </div>
              )}
              {ocrSaveMsg && (
                <p className={`mt-2 text-[10px] ${ocrSaveMsg.startsWith("✓") ? "text-emerald-400" : "text-red-400"}`}>
                  {ocrSaveMsg}
                </p>
              )}
            </div>

            {/* Trace info */}
            {trace && trace.status === "done" && (
              <div className="bg-rdp-hover/50 border border-rdp-border rounded p-3 text-[11px] space-y-1">
                <p className="text-slate-400 font-medium mb-1">Detected axes</p>
                {trace.calibration && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-slate-500">
                    <span>X axis</span>
                    <span className="text-slate-300">
                      {trace.calibration.x_lo} → {trace.calibration.x_hi}
                      {trace.calibration.x_step != null && ` (step ${trace.calibration.x_step})`}
                    </span>
                    <span>Y axis</span>
                    <span className="text-slate-300">
                      {trace.calibration.y_lo} → {trace.calibration.y_hi}
                      {trace.calibration.y_step != null && ` (step ${trace.calibration.y_step})`}
                    </span>
                    {trace.calibration.skew_deg !== 0 && (
                      <>
                        <span>Skew</span>
                        <span className="text-amber-400">{trace.calibration.skew_deg}°</span>
                      </>
                    )}
                  </div>
                )}
                <p className="text-slate-500 pt-1">
                  Curves: <span className="text-slate-300">{trace.curve_count}</span>
                  {" · "}Confidence:{" "}
                  <span className={(trace.confidence ?? 0) > 0.6 ? "text-emerald-400" : "text-amber-400"}>
                    {((trace.confidence ?? 0) * 100).toFixed(0)}%
                  </span>
                  {trace.tuned === false && (
                    <span className="ml-2 text-amber-500">⚠ not tuned (no metadata)</span>
                  )}
                </p>
                {trace.notes && trace.notes.length > 0 && (
                  <p className="text-slate-600 italic">{trace.notes.join(" · ")}</p>
                )}
              </div>
            )}
            {trace && trace.status === "running" && (
              <p className="text-xs text-blue-400 animate-pulse">● Finding points…</p>
            )}
            {trace && trace.status === "failed" && (
              <p className="text-xs text-red-400">✗ Trace failed: {trace.error}</p>
            )}

            {/* WPD exports — persistent snapshots of the current calibration.
                Sits next to the crop so an operator can save several variations
                per chart (e.g. one per curve-family layout) and re-download any
                of them without redoing the wizard. */}
            <div className="bg-rdp-hover/50 border border-rdp-border rounded p-3 text-[11px]">
              <div className="flex items-center justify-between mb-2">
                <p className="text-slate-400 font-medium">WPD exports</p>
                <span className="text-[9px] text-slate-500">
                  {exports.length} saved
                </span>
              </div>

              <div className="flex items-center gap-2 mb-2">
                <input
                  value={newExportName}
                  onChange={(e) => setNewExportName(e.target.value)}
                  placeholder="Name (e.g. variant-A)"
                  className="flex-1 bg-rdp-bg border border-slate-600 rounded px-2 py-0.5 text-slate-200 text-[11px]"
                  disabled={!hasCalibration}
                />
                <button
                  onClick={saveExport}
                  disabled={!hasCalibration || exportBusy}
                  className="text-[10px] px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-500 text-white disabled:opacity-50"
                  title={hasCalibration ? "Save the current calibration as a new WPD tar" : "Set both axes first via the wizard"}
                >
                  {exportBusy ? "Saving…" : "💾 Save Export"}
                </button>
                {/* Upload the .tar/.json the operator saved OUT of WPD as
                    the crop's final answer.  Listed alongside auto-snapshots
                    and manual saves, with an ⭐ marker so it stands out. */}
                <input
                  ref={uploadInputRef}
                  type="file"
                  accept=".tar,.json,application/x-tar,application/json"
                  hidden
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    e.target.value = "";  // let the same file be re-picked later
                    setExportBusy(true);
                    try {
                      const r = await api.batch.uploadExport(path, jobId, panel.name, f);
                      setExportMsg(`✓ Uploaded ${r.name} (${(r.uploaded_bytes / 1024).toFixed(1)} KB)`);
                      await refreshExports();
                    } catch (err) {
                      setExportMsg(`✗ Upload failed: ${err instanceof Error ? err.message : String(err)}`);
                    } finally {
                      setExportBusy(false);
                    }
                  }}
                />
                <button
                  onClick={() => uploadInputRef.current?.click()}
                  disabled={exportBusy}
                  className="text-[10px] px-2 py-1 rounded bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
                  title="Upload the .tar / .json you saved out of WPD as this crop's final answer"
                >
                  ⭐ Upload Final
                </button>
              </div>
              {!hasCalibration && (
                <p className="text-[10px] text-amber-400 mb-2">
                  Complete the wizard (both X and Y anchors) to enable Export.
                </p>
              )}
              {exportMsg && (
                <p className={`text-[10px] mb-2 ${exportMsg.startsWith("✓") ? "text-emerald-400" : "text-red-400"}`}>
                  {exportMsg}
                </p>
              )}

              {exports.length === 0 ? (
                <p className="text-slate-600 text-[10px]">No exports yet.</p>
              ) : (
                <ul className="space-y-1">
                  {exports.map((ex) => (
                    <li key={ex.name}
                        className={`flex items-center justify-between gap-2 rounded px-2 py-1 ${
                          ex.uploaded
                            ? "bg-amber-900/20 border border-amber-700/40"
                            : "bg-rdp-bg/50"
                        }`}>
                      <button
                        onClick={() => void loadExport(ex.name)}
                        className="flex-1 min-w-0 text-left hover:bg-rdp-bg/80 rounded px-1 py-0.5"
                        title={ex.uploaded
                          ? `Operator-uploaded final answer${ex.source_filename ? ` (from ${ex.source_filename})` : ""}`
                          : "Load this export back into the modal for editing"}
                      >
                        <p className={`truncate underline decoration-dotted underline-offset-2 ${
                          ex.uploaded ? "text-amber-300" : "text-emerald-300"
                        }`}>
                          {ex.uploaded ? "⭐" : "↺"} {ex.name}
                        </p>
                        <p className="text-[9px] text-slate-600">
                          {(ex.size / 1024).toFixed(1)} KB
                          {ex.saved_at && ` · ${new Date(ex.saved_at).toLocaleString()}`}
                          {ex.uploaded && ex.source_filename && ` · ${ex.source_filename}`}
                        </p>
                      </button>
                      <button
                        onClick={() => {
                          // Open placeholder tab synchronously (popup allowed),
                          // then swap its location once the round-trip finishes.
                          const win = window.open("about:blank", "_blank");
                          if (!win) { alert("Popup blocked — allow popups for this site."); return; }
                          void (async () => {
                            try {
                              const r = await api.batch.openExportInWpd(path, jobId, panel.name, ex.name);
                              const init = await api.hitl.init(r.job_id, r.chart_id);
                              win.location.href = init.wpd_editor_url;
                            } catch (e) {
                              win.close();
                              alert(`Failed to open in WPD: ${e instanceof Error ? e.message : String(e)}`);
                            }
                          })();
                        }}
                        className="text-[10px] text-blue-400 hover:text-blue-300 border border-blue-600/40 rounded px-1.5 py-0.5"
                        title="Open this saved export in WPD"
                      >
                        → WPD
                      </button>
                      <a
                        href={api.batch.exportUrl(path, jobId, panel.name, ex.name)}
                        download
                        className="text-[10px] text-emerald-400 hover:text-emerald-300 border border-emerald-600/40 rounded px-1.5 py-0.5"
                        title="Download this saved WPD tar"
                      >
                        ⤓ tar
                      </a>
                      <a
                        href={api.batch.exportJsonUrl(path, jobId, panel.name, ex.name)}
                        download
                        className="text-[10px] text-emerald-400 hover:text-emerald-300 border border-emerald-600/40 rounded px-1.5 py-0.5"
                        title="Download this export's wpd.json"
                      >
                        ⤓ json
                      </a>
                      <button
                        onClick={() => deleteExport(ex.name)}
                        className="text-[10px] text-slate-500 hover:text-red-400 border border-slate-700 rounded px-1.5 py-0.5"
                        title="Delete this export"
                      >
                        🗑
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-rdp-border px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex-1">
            {saveMsg && (
              <p className={`text-xs ${saveMsg.startsWith("✓") ? "text-emerald-400" : "text-red-400"}`}>
                {saveMsg}
              </p>
            )}
            {editMode && !rect && (
              <p className="text-xs text-slate-500">Drag on the source page to draw a new crop rectangle</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {editMode && rect && (
              <button
                onClick={saveCorrection}
                disabled={saving}
                className="text-xs px-3 py-1.5 rounded bg-orange-600 hover:bg-orange-500 text-white disabled:opacity-50"
              >
                {saving ? "Saving…" : "Save New Crop"}
              </button>
            )}
            {editMode && (
              <button
                onClick={() => { setRect(null); setEditMode(false); redraw(null); }}
                className="text-xs px-3 py-1.5 rounded border border-slate-600 text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
            )}
            {/* Run every registered chart-tracing handler on this crop and
                open a picker so the operator eyeballs the eleven overlays
                and hands the best one to WPD. */}
            <button
              onClick={() => setAllTracersOpen(true)}
              className="text-xs px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white"
              title="Run all chart-tracing handlers on this crop and pick the best overlay"
            >
              🎨 Try all tracers
            </button>
            {/* Quick download of the current calibration.  For persistent
                snapshots (multiple variants per crop) use the WPD exports
                panel on the right side of the modal. */}
            {hasCalibration ? (
              <a
                href={api.batch.wpdTarUrl(path, jobId, panel.name)}
                download
                className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white"
                title="Download a WPD 4.7 project (info.json + wpd.json + crop image) with the calibration you just set"
              >
                📦 Download WPD tar
              </a>
            ) : (
              <button
                disabled
                className="text-xs px-3 py-1.5 rounded bg-slate-700 text-slate-500 cursor-not-allowed"
                title="Set both axes first via the wizard"
              >
                📦 Export WPD tar (calibrate first)
              </button>
            )}
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded border border-slate-600 text-slate-400 hover:text-slate-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
    {allTracersOpen && (
      <AllTracersModal
        path={path}
        jobId={jobId}
        filename={panel.name}
        onClose={() => setAllTracersOpen(false)}
      />
    )}
    </>
  );
}


/**
 * Circular magnifier that follows the cursor while placing markers.
 *
 * It renders the source crop as a CSS background scaled by `zoom`, offset so
 * the pixel under the cursor lands at the loupe's centre.  Positioning uses
 * percentages of the crop container so the loupe tracks the cursor regardless
 * of how the browser scaled the underlying image.  A crosshair inside the
 * loupe pinpoints the exact pixel the click / release will commit to.
 */
function Loupe({
  src,
  imgW,
  imgH,
  hover,
  zoom,
  size,
  anchors,
}: {
  src: string;
  imgW: number;
  imgH: number;
  hover: { x: number; y: number };
  zoom: number;
  size: number;
  anchors?: { x: number[]; y: number[] };
}) {
  const half = size / 2;
  // Anchor to the natural-image px, then offset up-right so the loupe sits
  // above the cursor instead of covering it.
  const leftPct = (hover.x / imgW) * 100;
  const topPct = (hover.y / imgH) * 100;
  // If the cursor is near the top of the image, flip the loupe below instead.
  const flipDown = topPct < 30;
  const flipLeft = leftPct > 70;
  const offsetX = flipLeft ? -(size + 24) : 24;
  const offsetY = flipDown ? 24 : -(size + 24);

  // Convert an image px to loupe px so anchor pips can be drawn on top of the
  // magnified region — this is what lets the operator align a new Y anchor
  // with the existing X anchor while looking through the loupe.
  const toLoupePx = (px: number, py: number) => ({
    x: half + (px - hover.x) * zoom,
    y: half + (py - hover.y) * zoom,
  });

  const xAnchors = anchors?.x ?? [];
  const yAnchors = anchors?.y ?? [];

  return (
    <div
      className="absolute pointer-events-none z-20"
      style={{
        left: `${leftPct}%`,
        top: `${topPct}%`,
        transform: `translate(${offsetX}px, ${offsetY}px)`,
        width: size,
        height: size,
      }}
    >
      <div
        className="absolute inset-0 rounded-full overflow-hidden border-2 border-orange-500 shadow-lg shadow-black/40"
        style={{
          backgroundImage: `url(${src})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: `${imgW * zoom}px ${imgH * zoom}px`,
          backgroundPosition: `${half - hover.x * zoom}px ${half - hover.y * zoom}px`,
          backgroundColor: "#0f172a",
        }}
      >
        {/* SVG overlay inside the loupe for anchor pips + crosshair.
            Prior anchors are drawn thick and bright so an operator placing
            y₁ can see exactly where x₁ / xₙ sit and align the crosshair
            with them.  A small filled circle marks the intersection of
            each anchor line with the loupe's centre-row/column so the
            landing point of a click is unambiguous. */}
        <svg
          className="absolute inset-0 w-full h-full"
          viewBox={`0 0 ${size} ${size}`}
        >
          {xAnchors.map((px, i) => {
            const p = toLoupePx(px, hover.y);
            return (
              <g key={`lx${i}`}>
                <line
                  x1={p.x} y1={0} x2={p.x} y2={size}
                  stroke="#f97316" strokeWidth={2.5} strokeDasharray="5 3" opacity={0.95}
                />
                <circle cx={p.x} cy={half} r={4.5} fill="#f97316" stroke="#000" strokeWidth={1} />
              </g>
            );
          })}
          {yAnchors.map((py, i) => {
            const p = toLoupePx(hover.x, py);
            return (
              <g key={`ly${i}`}>
                <line
                  x1={0} y1={p.y} x2={size} y2={p.y}
                  stroke="#f97316" strokeWidth={2.5} strokeDasharray="5 3" opacity={0.95}
                />
                <circle cx={half} cy={p.y} r={4.5} fill="#f97316" stroke="#000" strokeWidth={1} />
              </g>
            );
          })}
          {/* Crosshair for the cursor's exact pixel */}
          <line x1={half} y1={0} x2={half} y2={size} stroke="#fbbf24" strokeWidth={1.5} opacity={0.95} />
          <line x1={0} y1={half} x2={size} y2={half} stroke="#fbbf24" strokeWidth={1.5} opacity={0.95} />
          <circle cx={half} cy={half} r={5} fill="none" stroke="#fbbf24" strokeWidth={2} />
        </svg>
      </div>
      {/* px readout under loupe */}
      <div
        className="absolute left-1/2 -translate-x-1/2 mt-1 text-[10px] font-mono text-orange-400 bg-black/60 px-1.5 py-0.5 rounded whitespace-nowrap"
        style={{ top: size }}
      >
        {hover.x}, {hover.y}
      </div>
    </div>
  );
}


/**
 * SVG overlay drawn on top of the crop image.
 *
 * OCR reports each tick's pixel center on the natural image; the SVG uses a
 * viewBox matching those natural dimensions and stretches to fill the img's
 * rendered box, so the markers land on the same pixels regardless of how the
 * browser scaled the img.  x₁/xₙ and y₁/yₙ get emphasised labels because the
 * spec calls out the extremes explicitly.
 *
 * When `interactive` is true, marker circles become draggable handles and
 * clicks in the axis strips (bottom for X, left for Y) call `onClickEmpty`
 * with the click's pixel coordinates so the parent can append a new tick.
 */
function AxisOverlay({
  ocr,
  imgW,
  imgH,
  interactive = false,
  wizardActive = false,
  dragMarker = null,
  rotationDeg = 0,
  onStartDrag,
  onDrag,
  onEndDrag,
  onImageClick,
  onHover,
}: {
  ocr: BatchOcr;
  imgW: number;
  imgH: number;
  interactive?: boolean;
  wizardActive?: boolean;
  dragMarker?: { axis: "x" | "y"; index: number } | null;
  /** Deskew rotation applied to the parent wrapper via CSS.  Positive
   *  values mean the wrapper is rotated counter-clockwise on screen.
   *  Clicks must be inverse-rotated to land on the un-rotated image
   *  pixels that the calibration is stored against. */
  rotationDeg?: number;
  onStartDrag?: (axis: "x" | "y", index: number) => void;
  onDrag?: (px: number, py: number) => void;
  onEndDrag?: (px: number, py: number) => void;
  onImageClick?: (px: number, py: number) => void;
  onHover?: (pt: { x: number; y: number } | null) => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);

  // Convert an event's client coordinates back to natural image pixels.
  // With CSS rotation on the parent, `getBoundingClientRect` returns the
  // axis-aligned bounding box of the ROTATED element, not the layout box.
  // Falling back to the wrapper's `offsetWidth`/`offsetHeight` gives the
  // pre-rotation size, and an inverse-rotation around the visible centre
  // maps the click back to the un-rotated image frame that anchor
  // positions are stored against.
  const toImagePx = (clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const r = svg.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    let dx = clientX - cx;
    let dy = clientY - cy;
    if (rotationDeg) {
      const rad = (rotationDeg * Math.PI) / 180;   // undo wrapper rotation
      const c = Math.cos(rad);
      const s = Math.sin(rad);
      const rx = dx * c + dy * s;
      const ry = -dx * s + dy * c;
      dx = rx;
      dy = ry;
    }
    // Pre-rotation width in screen pixels.  When the wrapper has
    // `transform: rotate(θ)` its layout box is unchanged, so
    // `offsetWidth`/`offsetHeight` still describe the un-rotated size.
    const parent = svg.parentElement as HTMLElement | null;
    const preW = parent ? parent.offsetWidth : r.width;
    const preH = parent ? parent.offsetHeight : r.height;
    return {
      x: Math.round(((dx + preW / 2) / preW) * imgW),
      y: Math.round(((dy + preH / 2) / preH) * imgH),
    };
  };

  // Global mouse listeners while dragging — so releasing outside the SVG
  // still commits the move.
  useEffect(() => {
    if (!interactive || !dragMarker) return;
    const move = (e: MouseEvent) => {
      const p = toImagePx(e.clientX, e.clientY);
      onDrag?.(p.x, p.y);
    };
    const up = (e: MouseEvent) => {
      const p = toImagePx(e.clientX, e.clientY);
      onEndDrag?.(p.x, p.y);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, dragMarker]);

  const xs = ocr.x_ticks ?? [];
  const ys = ocr.y_ticks ?? [];
  const xpos_raw = ocr.x_positions ?? [];
  const ypos_raw = ocr.y_positions ?? [];
  // Per-anchor click y for x anchors and click x for y anchors.  Fall back
  // to sensible defaults (image edges) for records saved before we tracked
  // the "other" coordinate.
  const xpy_raw = ocr.x_positions_py ?? [];
  const ypx_raw = ocr.y_positions_px ?? [];
  const lineLabels = ocr.line_labels ?? [];
  const linePos = ocr.line_positions ?? [];

  // Two-anchor model: only x₁/xₙ and y₁/yₙ get draggable circles; every
  // other value in x_ticks / y_ticks paints a thin grid line whose pixel
  // position is interpolated between the two anchors.  This means when the
  // operator types "1, 2, ..., 10" in the edit form, the axis instantly
  // shows ten gridlines the moment they save — no need to hand-place each
  // one.
  const interpolatePositions = (values: number[], anchors: number[]): number[] => {
    if (values.length === 0 || anchors.length === 0) return [];
    if (values.length === 1) return [anchors[0]];
    const px1 = anchors[0];
    const pxN = anchors[anchors.length - 1];
    const v1 = values[0];
    const vN = values[values.length - 1];
    if (vN === v1) return values.map(() => px1);
    return values.map(v => Math.round(px1 + ((v - v1) / (vN - v1)) * (pxN - px1)));
  };
  const xpos = interpolatePositions(xs, xpos_raw);
  const ypos = interpolatePositions(ys, ypos_raw);

  // Edges are the first and last tick (or, if only 1 value/pos, just [0]).
  const xEdges = xs.length >= 2 ? [0, xs.length - 1] : xs.length === 1 ? [0] : [];
  const yEdges = ys.length >= 2 ? [0, ys.length - 1] : ys.length === 1 ? [0] : [];
  // Slim dots and thin dashed lines per user request.
  const ref = Math.min(imgW, imgH);
  const fs = Math.max(14, Math.round(ref * 0.07));
  const stroke = Math.max(1, Math.round(ref * 0.003));
  const dot = Math.max(4, Math.round(ref * 0.011));

  const handleBgClick = (e: React.MouseEvent<SVGRectElement>) => {
    if (!interactive || !onImageClick) return;
    const p = toImagePx(e.clientX, e.clientY);
    onImageClick(p.x, p.y);
  };

  return (
    <svg
      ref={svgRef}
      className={`absolute top-0 left-0 w-full h-full ${interactive ? "pointer-events-auto" : "pointer-events-none"}`}
      viewBox={`0 0 ${imgW} ${imgH}`}
      preserveAspectRatio="none"
      onMouseMove={(e) => {
        if (!interactive || !onHover) return;
        onHover(toImagePx(e.clientX, e.clientY));
      }}
      onMouseLeave={() => onHover?.(null)}
    >
      {/* Invisible background hit-area for click-to-add.  In wizard mode the
          whole image is a hit target; otherwise clicks still register through
          the rect and the modal decides based on strip position. */}
      {interactive && (
        <rect
          x={0} y={0} width={imgW} height={imgH}
          fill="transparent"
          onClick={handleBgClick}
          style={{ cursor: wizardActive ? "crosshair" : "default" }}
        />
      )}

      {/* X ticks — thin dashed lines everywhere, but only x₁ / xₙ get a
          draggable anchor circle.  Middle ticks stay as a tiny reference pip
          so the reader can still see the OCR readout without clutter. */}
      {xs.map((v, i) => {
        const px = xpos[i];
        if (px == null) return null;
        const isEdge = xEdges.includes(i);
        const beingDragged = dragMarker?.axis === "x" && dragMarker.index === i;
        return (
          <g key={`x${i}`}>
            <line
              x1={px} y1={0} x2={px} y2={imgH}
              stroke={beingDragged ? "#fbbf24" : "#f97316"}
              strokeWidth={stroke}
              strokeDasharray={`${stroke * 4} ${stroke * 4}`}
              opacity={isEdge || beingDragged ? 0.7 : 0.2}
            />
            {(() => {
              // Landing y for this x-anchor: the operator's click py if we
              // have it (index 0 for x_start, index -1 for x_end).  Middle
              // ticks fall back to the image bottom edge.
              const cy = i === 0
                ? (xpy_raw[0] ?? (imgH - dot * 2))
                : i === xs.length - 1
                  ? (xpy_raw[xpy_raw.length - 1] ?? (imgH - dot * 2))
                  : (imgH - dot * 2);
              return isEdge ? (
                <circle
                  cx={px} cy={cy}
                  r={interactive ? dot * 1.3 : dot * 0.9}
                  fill={beingDragged ? "#fbbf24" : "#f97316"}
                  stroke="#000" strokeWidth={0.5}
                  onMouseDown={interactive && !wizardActive ? (e) => {
                    e.stopPropagation();
                    onStartDrag?.("x", i);
                  } : undefined}
                  style={interactive && !wizardActive
                    ? { cursor: beingDragged ? "grabbing" : "grab" }
                    : { pointerEvents: "none" }}
                />
              ) : (
                <circle cx={px} cy={cy} r={dot * 0.35} fill="#f97316" opacity={0.55} pointerEvents="none" />
              );
            })()}
            {isEdge && (
              <text
                x={px}
                y={(i === 0
                     ? (xpy_raw[0] ?? (imgH - dot * 2))
                     : (xpy_raw[xpy_raw.length - 1] ?? (imgH - dot * 2))) - dot * 1.5}
                textAnchor="middle"
                fontSize={fs}
                fontWeight="bold"
                fill="#fff"
                stroke="#000"
                strokeWidth={3}
                paintOrder="stroke"
                pointerEvents="none"
              >
                {i === 0 ? `X-LEFT=${v}` : `X-RIGHT=${v}`}
              </text>
            )}
          </g>
        );
      })}

      {/* Y ticks — same two-anchor rule. */}
      {ys.map((v, i) => {
        const py = ypos[i];
        if (py == null) return null;
        const isEdge = yEdges.includes(i);
        const beingDragged = dragMarker?.axis === "y" && dragMarker.index === i;
        return (
          <g key={`y${i}`}>
            <line
              x1={0} y1={py} x2={imgW} y2={py}
              stroke={beingDragged ? "#fbbf24" : "#f97316"}
              strokeWidth={stroke}
              strokeDasharray={`${stroke * 4} ${stroke * 4}`}
              opacity={isEdge || beingDragged ? 0.7 : 0.2}
            />
            {(() => {
              const cx = i === 0
                ? (ypx_raw[0] ?? (dot * 2))
                : i === ys.length - 1
                  ? (ypx_raw[ypx_raw.length - 1] ?? (dot * 2))
                  : (dot * 2);
              return isEdge ? (
                <circle
                  cx={cx} cy={py}
                  r={interactive ? dot * 1.3 : dot * 0.9}
                  fill={beingDragged ? "#fbbf24" : "#f97316"}
                  stroke="#000" strokeWidth={0.5}
                  onMouseDown={interactive && !wizardActive ? (e) => {
                    e.stopPropagation();
                    onStartDrag?.("y", i);
                  } : undefined}
                  style={interactive && !wizardActive
                    ? { cursor: beingDragged ? "grabbing" : "grab" }
                    : { pointerEvents: "none" }}
                />
              ) : (
                <circle cx={cx} cy={py} r={dot * 0.35} fill="#f97316" opacity={0.55} pointerEvents="none" />
              );
            })()}
            {isEdge && (
              <text
                x={(i === 0
                     ? (ypx_raw[0] ?? (dot * 2))
                     : (ypx_raw[ypx_raw.length - 1] ?? (dot * 2))) + dot * 1.5}
                y={py + fs * 0.35}
                fontSize={fs}
                fontWeight="bold"
                fill="#fff"
                stroke="#000"
                strokeWidth={3}
                paintOrder="stroke"
                pointerEvents="none"
              >
                {i === 0 ? `Y-BOTTOM=${v}` : `Y-TOP=${v}`}
              </text>
            )}
          </g>
        );
      })}

      {/* On-line labels */}
      {lineLabels.map((v, i) => {
        const p = linePos[i];
        if (!p) return null;
        const [cx, cy] = p;
        return (
          <g key={`l${i}`}>
            <circle cx={cx} cy={cy} r={dot * 1.2} fill="#22d3ee" opacity={0.75} stroke="#000" strokeWidth={1} pointerEvents="none" />
            <text
              x={cx + dot * 2} y={cy + fs * 0.35}
              fontSize={fs * 0.9}
              fontWeight="bold"
              fill="#22d3ee"
              stroke="#000"
              strokeWidth={3}
              paintOrder="stroke"
              pointerEvents="none"
            >
              {v}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
